/* eslint-disable prefer-destructuring */
import { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import { type BasketTerms } from "@shared/modules/basket/utils/basket-utils";
import { VenueBasedEventAccessSerializer } from "../../events/data/types/event-accesses";
import { VenueBasedMembershipLevelSerializer } from "../../memberships/data/types/membership-levels";
import { VenueBasedTicketTypeInventorySerializer } from "../data/types/inventory";
import { TicketTypeSerializer } from "../../events/data/types/events";

/**
 * Filters inventories to only include ticket types that exist in the event's tickettype_set.
 * This is used to filter out membership ticket types from the Box Office Tickets tab,
 * as membership tickets should only be accessible through the Memberships tab.
 *
 * @param inventories - The inventories from the event access response
 * @param tickettypeSet - The tickettype_set from the event
 * @returns Filtered inventories containing only ticket types that exist in the event's tickettype_set
 */
export const filterInventoriesByEventTicketTypes = (
  inventories: VenueBasedTicketTypeInventorySerializer[] | undefined,
  tickettypeSet: TicketTypeSerializer[] | undefined
): VenueBasedTicketTypeInventorySerializer[] => {
  if (!inventories || !tickettypeSet) {
    return [];
  }

  // Create a Set of ticket type IDs from the event's tickettype_set for O(1) lookup
  const eventTicketTypeIds = new Set(tickettypeSet.map((tt) => tt.id));

  // Filter inventories to only include those whose ticket_type.id exists in the event's tickettype_set
  return inventories.filter((inventory) =>
    eventTicketTypeIds.has(inventory.ticket_type.id)
  );
};

/**
 * Accepts event accesses and checks the item groups in the basket for one that matches the event id.
 * If an item group matches an event, it will return the event from that event access, with hasAcceptedTerms set to the item groups accepted_terms value.
 * Also considers parent-child relationships between item groups, so terms from child items will include their parent items.
 * Groups events by terms_url_link and concatenates names if different events share the same terms_url_link.
 *
 * @param eventAccesses - The event accesses to check for matching events.
 * @param membershipLevels - The membership levels to check for terms requirements.
 * @param itemGroups - The item groups in the basket.
 *
 * @returns The events in the basket that have terms and conditions, deduplicated by terms_url_link.
 */
export const getTermsInBasket = (
  eventAccesses: Array<VenueBasedEventAccessSerializer>,
  membershipLevels: Array<VenueBasedMembershipLevelSerializer>,
  itemGroups: Array<TicketItemGroupType>
): Array<BasketTerms> => {
  // Build a child-to-parent map to track item group hierarchies
  const childToParentMap = new Map<number, number>();
  itemGroups.forEach((ig) => {
    if (ig.id && ig.parent) {
      childToParentMap.set(ig.id, ig.parent);
    }
  });

  // Helper function to collect all parent item group IDs for a given set of item group IDs
  const collectParentItemGroupIds = (
    directItemGroupIds: number[]
  ): Set<number> => {
    const parentItemGroupIds = new Set<number>();

    directItemGroupIds.forEach((igId) => {
      // Find immediate parent
      let currentParentId = childToParentMap.get(igId);

      // Traverse up the parent chain
      while (currentParentId !== undefined) {
        parentItemGroupIds.add(currentParentId);
        currentParentId = childToParentMap.get(currentParentId);
      }
    });

    return parentItemGroupIds;
  };

  // Pre-process item groups into maps for O(1) lookup
  const eventItemGroups = new Map<number, TicketItemGroupType[]>();
  const membershipItemGroups = new Map<number, TicketItemGroupType[]>();

  // O(n) where n is number of item groups
  itemGroups.forEach((ig) => {
    if (typeof ig.event === "number") {
      const groups = eventItemGroups.get(ig.event) || [];
      groups.push(ig);
      eventItemGroups.set(ig.event, groups);
    }
    if (typeof ig.membership_level === "number") {
      const groups = membershipItemGroups.get(ig.membership_level) || [];
      groups.push(ig);
      membershipItemGroups.set(ig.membership_level, groups);
    }
  });

  // Handle event terms - O(e) where e is number of event accesses
  const eventTerms = eventAccesses.reduce(
    (carry: Array<BasketTerms>, eventAccess) => {
      const { event } = eventAccess;
      if (
        !event.require_terms_acceptance ||
        !event.terms_url_link ||
        typeof event.id !== "number"
      )
        return carry;

      const relevantGroups = eventItemGroups.get(event.id) || [];
      if (relevantGroups.length === 0) return carry;

      // Get direct item group IDs
      const directItemGroupIds = relevantGroups.map((ig) => ig.id as number);

      // Collect parent item group IDs
      const parentItemGroupIds = collectParentItemGroupIds(directItemGroupIds);

      // Combined direct and parent item group IDs
      const allItemGroupIds = [
        ...directItemGroupIds,
        ...Array.from(parentItemGroupIds),
      ];

      // Check if all direct item groups have accepted terms
      // Note: We only check direct groups for acceptance status, not parents
      const hasAcceptedTerms = relevantGroups.every((ig) => ig.accepted_terms);

      carry.push({
        id: event.id,
        name: event.name ?? "",
        itemGroupIds: allItemGroupIds, // Include both direct and parent item groups
        hasAcceptedTerms,
        require_terms_acceptance: event.require_terms_acceptance,
        terms_url_link: event.terms_url_link,
        parent_event: event.parent_event,
      });
      return carry;
    },
    []
  );

  // Handle membership terms - O(m) where m is number of membership levels
  const membershipTerms = membershipLevels.reduce(
    (carry: Array<BasketTerms>, membership) => {
      if (
        !membership.require_terms_acceptance ||
        !membership.terms_url_link ||
        typeof membership.id !== "number"
      )
        return carry;

      const relevantGroups = membershipItemGroups.get(membership.id) || [];
      if (relevantGroups.length === 0) return carry;

      // Get direct item group IDs
      const directItemGroupIds = relevantGroups.map((ig) => ig.id as number);

      // Collect parent item group IDs
      const parentItemGroupIds = collectParentItemGroupIds(directItemGroupIds);

      // Combined direct and parent item group IDs
      const allItemGroupIds = [
        ...directItemGroupIds,
        ...Array.from(parentItemGroupIds),
      ];

      // TO-DO: membership levels return null for acceptance terms. Not a big deal at the moment.
      // Default acceptance for terms to true for box office;
      // Check if all memberships levels have accepted terms
      // Returns true if accepted_terms is true or null/undefined (box office default)
      // Returns false if any membership has explicitly set accepted_terms to false
      const hasAcceptedTerms = relevantGroups.every(
        (ig) => ig.accepted_terms ?? true
      );

      carry.push({
        id: membership.id,
        name: membership.name ?? "",
        itemGroupIds: allItemGroupIds, // Include both direct and parent item groups
        hasAcceptedTerms,
        require_terms_acceptance: membership.require_terms_acceptance,
        terms_url_link: membership.terms_url_link,
      });
      return carry;
    },
    []
  );

  // Combine event and membership terms
  const allTerms = [...eventTerms, ...membershipTerms];

  // Now deduplicate by terms_url_link and concatenate names where needed
  const termsByUrl = new Map<string, BasketTerms>();
  // Keep track of all names for each terms_url_link
  const namesByUrl = new Map<string, Set<string>>();

  allTerms.forEach((term) => {
    if (!term.terms_url_link) return;

    const existingTerm = termsByUrl.get(term.terms_url_link);

    if (!existingTerm) {
      // First term with this URL
      termsByUrl.set(term.terms_url_link, { ...term });
      namesByUrl.set(term.terms_url_link, new Set([term.name]));
    } else {
      // Merge with existing term

      // Add name to the collection if it's from a different item
      if (existingTerm.id !== term.id) {
        const names = namesByUrl.get(term.terms_url_link) || new Set();
        names.add(term.name);
        namesByUrl.set(term.terms_url_link, names);

        // Format the name with proper grammar
        const namesArray = Array.from(names);
        if (namesArray.length === 1) {
          existingTerm.name = namesArray[0];
        } else if (namesArray.length === 2) {
          const [first, second] = namesArray;
          existingTerm.name = `${first} and ${second}`;
        } else {
          const lastItem = namesArray.pop();
          existingTerm.name = `${namesArray.join(", ")} and ${lastItem}`;
        }
      }

      // Merge item group IDs
      existingTerm.itemGroupIds = [
        ...new Set([...existingTerm.itemGroupIds, ...term.itemGroupIds]),
      ];

      // Update hasAcceptedTerms (all must be accepted)
      existingTerm.hasAcceptedTerms =
        existingTerm.hasAcceptedTerms && term.hasAcceptedTerms;
    }
  });

  return Array.from(termsByUrl.values());
};
